import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function UserManagement() {
  const { t } = useI18n();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "viewer" });
  const [busy, setBusy] = useState(false);

  const isAdmin = (currentUser?.role || "").toLowerCase() === "admin";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn("users load failed:", err?.message || err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.email || !form.password) return;
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(t("userCreated"));
      setForm({ email: "", name: "", password: "", role: "viewer" });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (email, role) => {
    try {
      await api.patch(`/users/${encodeURIComponent(email)}`, { role });
      toast.success(t("userUpdated"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || String(err));
    }
  };

  const remove = async (email) => {
    if (!window.confirm(t("userDeleteConfirm", { email }))) return;
    try {
      await api.delete(`/users/${encodeURIComponent(email)}`);
      toast.success(t("userDeleted"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || String(err));
    }
  };

  return (
    <div className="mq-panel" data-testid="user-management-panel">
      <div className="mq-panel-header">
        <span className="mq-heading text-sm font-semibold tracking-wider">{t("usersTitle")}</span>
        {isAdmin && (
          <button
            data-testid="user-new-btn"
            className="mq-btn mq-btn-primary"
            onClick={() => setShowForm((v) => !v)}
            style={{ padding: "0.3rem 0.75rem" }}
          >
            {showForm ? t("cancel") : t("userNewBtn")}
          </button>
        )}
      </div>

      {showForm && isAdmin && (
        <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-5 gap-2 border-b" style={{ borderColor: "var(--mq-border)" }} data-testid="user-form">
          <input data-testid="user-form-email" className="mq-input text-xs" placeholder={t("userEmail")}
                 value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="off" />
          <input data-testid="user-form-name" className="mq-input text-xs" placeholder={t("userName")}
                 value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="off" />
          <input data-testid="user-form-password" className="mq-input text-xs" placeholder={t("userPassword")}
                 type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                 autoComplete="off" data-lpignore="true" data-1p-ignore="true" />
          <select data-testid="user-form-role" className="mq-input text-xs"
                  value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="viewer">viewer</option>
            <option value="admin">admin</option>
          </select>
          <button data-testid="user-form-submit" className="mq-btn mq-btn-primary text-xs"
                  onClick={create} disabled={busy}>{t("create")}</button>
        </div>
      )}

      <table className="w-full text-xs font-mono">
        <thead>
          <tr className="text-left mq-label">
            <th className="px-4 py-2">{t("userEmail")}</th>
            <th className="px-2 py-2">{t("userName")}</th>
            <th className="px-2 py-2">{t("userRole")}</th>
            <th className="px-4 py-2 text-right">{t("actions")}</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.email} data-testid={`user-row-${u.email}`} className="border-t" style={{ borderColor: "var(--mq-border)" }}>
              <td className="px-4 py-2">{u.email}</td>
              <td className="px-2 py-2 text-[var(--mq-text-2)]">{u.name || "—"}</td>
              <td className="px-2 py-2 uppercase" style={{ color: u.role === "admin" ? "var(--mq-green)" : "var(--mq-yellow)" }}>
                {u.role}
              </td>
              <td className="px-4 py-2 text-right">
                {isAdmin && u.email !== currentUser?.email && (
                  <>
                    <button
                      data-testid={`user-role-toggle-${u.email}`}
                      className="mq-btn text-xs mr-2"
                      onClick={() => changeRole(u.email, u.role === "admin" ? "viewer" : "admin")}
                      style={{ padding: "0.2rem 0.5rem" }}
                    >
                      → {u.role === "admin" ? "viewer" : "admin"}
                    </button>
                    <button
                      data-testid={`user-delete-${u.email}`}
                      className="mq-btn text-xs"
                      onClick={() => remove(u.email)}
                      style={{ padding: "0.2rem 0.5rem", borderColor: "var(--mq-red)", color: "var(--mq-red)" }}
                    >
                      {t("delete")}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
